<template>
  <SectionPageShell section="settings" title="Einstellungen">
    <Form @submit="submit" v-slot="{ errors }" :initial-values="initialValues" :validation-schema="schema">
      <div class="row">
        <div class="col-12 col-lg-6">
          <label for="first_name">Vorname</label>
          <Field type="text" as="input" id="first_name" name="first_name" class="form-control"
                 :class="{'is-invalid': errors.first_name}" autocomplete="off" aria-required="true"
          />
          <ErrorMessage name="first_name" class="invalid-feedback d-block" />
        </div>
        <div class="col-12 col-lg-6">
          <label for="last_name">Nachname</label>
          <Field type="text" as="input" id="last_name" name="last_name" class="form-control"
                 :class="{'is-invalid': errors.last_name}" autocomplete="off" aria-required="true"
          />
          <ErrorMessage name="last_name" class="invalid-feedback d-block" />
        </div>
        <div v-if="hasCurrentOrganisation" class="col-12 col-lg-6 mt-3">
          <label for="phone">Telefon</label>
          <Field type="tel" as="input" id="phone" name="phone" class="form-control"
                 :class="{'is-invalid': errors.phone}" autocomplete="tel"
          />
          <ErrorMessage name="phone" class="invalid-feedback d-block" />
        </div>
      </div>
      <button type="submit" class="btn btn-primary mt-3">Speichern</button>
    </Form>

    <section v-if="passwordChangeSupported" class="account-password mt-5 pt-4">
      <h2 class="h4">Passwort ändern</h2>
      <p class="text-muted mb-4">
        Nach der Änderung werden Sie auf allen Geräten abgemeldet und können sich mit dem neuen Passwort erneut anmelden.
      </p>

      <Form
        @submit="submitPassword"
        v-slot="{ errors }"
        :validation-schema="passwordSchema"
      >
        <div class="row">
          <div class="col-12 col-lg-6">
            <label for="current_password">Aktuelles Passwort</label>
            <Field
              type="password"
              as="input"
              id="current_password"
              name="current_password"
              class="form-control"
              :class="{'is-invalid': errors.current_password}"
              autocomplete="current-password"
              aria-required="true"
            />
            <ErrorMessage name="current_password" class="invalid-feedback d-block" />
          </div>
          <div class="col-12 col-lg-6 mt-3 mt-lg-0">
            <label for="password">Neues Passwort</label>
            <Field
              type="password"
              as="input"
              id="password"
              name="password"
              class="form-control"
              :class="{'is-invalid': errors.password}"
              autocomplete="new-password"
              aria-required="true"
            />
            <ErrorMessage name="password" class="invalid-feedback d-block" />
          </div>
          <div class="col-12 col-lg-6 mt-3">
            <label for="password_confirmation">Neues Passwort bestätigen</label>
            <Field
              type="password"
              as="input"
              id="password_confirmation"
              name="password_confirmation"
              class="form-control"
              :class="{'is-invalid': errors.password_confirmation}"
              autocomplete="new-password"
              aria-required="true"
            />
            <ErrorMessage name="password_confirmation" class="invalid-feedback d-block" />
          </div>
        </div>
        <button type="submit" class="btn btn-primary mt-3" :disabled="passwordSaving">
          {{ passwordSaving ? 'Passwort wird gespeichert …' : 'Passwort ändern' }}
        </button>
      </Form>
    </section>

    <section class="account-personal-data mt-5 pt-4">
      <h2 class="h4">Persönliche Daten</h2>
      <p class="text-muted mb-4">
        Laden Sie eine maschinenlesbare Kopie der persönlichen Daten herunter, die wir zu Ihrem Konto gespeichert haben.
      </p>
      <button
        type="button"
        class="btn btn-outline-primary"
        :disabled="personalDataDownloading"
        @click="downloadPersonalData"
      >
        {{ personalDataDownloading ? 'Daten werden erstellt …' : 'Persönliche Daten herunterladen' }}
      </button>
      <div v-if="canRequestAccountDeletion" class="mt-4">
        <p>Sie können die Löschung Ihres Accounts und sämtlicher zugehöriger Kundendaten anfragen.
          Wir prüfen Ihre Anfrage und informieren Sie per E-Mail, sobald die Löschung abgeschlossen ist.</p>
        <p v-if="userStore.user.deletion_request_pending" role="status" class="text-success">
          Ihre Löschanfrage ist eingegangen und wird bearbeitet.
        </p>
        <button v-else type="button" class="btn btn-outline-danger"
                :disabled="deletionRequestSending" @click="requestAccountDeletion">
          {{ deletionRequestSending ? 'Anfrage wird gesendet …' : 'Account-Löschung anfragen' }}
        </button>
      </div>
    </section>
  </SectionPageShell>
</template>

<script>
import SectionPageShell from "@/components/base_type/SectionPageShell.vue";
import {useUserStore} from "@/stores/user";
import { Form, Field, ErrorMessage } from "vee-validate";
import Yup from "@/yup";
import Swal from "sweetalert2";
import {Helpers} from "@/helpers";

export default {
  components: {
    ErrorMessage,
    Field,
    Form,
    SectionPageShell,
  },
  setup() {
    return {
      userStore: useUserStore(),
    }
  },
  data() {
    return {
      schema: Yup.object({
        first_name: Yup.string().required(),
        last_name: Yup.string().required(),
        phone: Yup.string().phone('DE', false, 'Bitte geben Sie eine gültige Telefonnummer ein.'),
      }),
      passwordSchema: Yup.object({
        current_password: Yup.string().required('Das aktuelle Passwort ist ein Pflichtfeld.'),
        password: Yup.string()
            .required('Das neue Passwort ist ein Pflichtfeld.')
            .min(8, 'Das Passwort muss mindestens 8 Zeichen lang sein.')
            .matches(
                /^(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/,
                'Das Passwort muss mindestens ein Sonderzeichen und eine Zahl enthalten.'
            )
            .notOneOf([Yup.ref('current_password')], 'Das neue Passwort muss sich vom aktuellen Passwort unterscheiden.'),
        password_confirmation: Yup.string()
            .required('Die Passwortbestätigung ist ein Pflichtfeld.')
            .oneOf([Yup.ref('password')], 'Die Passwörter müssen übereinstimmen.'),
      }),
      passwordSaving: false,
      personalDataDownloading: false,
      deletionRequestSending: false,
      initialValues: {
        first_name: this.userStore.user.first_name,
        last_name: this.userStore.user.last_name,
        phone: this.userStore.user.organisation?.phone || '',
      },
    }
  },
  computed: {
    hasCurrentOrganisation() {
      return this.userStore.user.currentOrganisationPresent()
    },
    canRequestAccountDeletion() {
      const groups = this.userStore.user.groups || {}
      return !this.userStore.isImpersonating && !groups.manetStaff && !groups.manetAdmin
    },
    passwordChangeSupported() {
      return this.userStore.user.password_change_supported
    },
  },
  methods: {
    submit(values) {
      const userValues = {
        first_name: values.first_name,
        last_name: values.last_name,
      }
      const requests = [
        this.userStore.apiUpdateUserData(userValues),
      ]

      if (this.hasCurrentOrganisation) {
        requests.push(this.userStore.apiUpdateCurrentClientData({
          phone: values.phone || '',
        }))
      }

      Promise.all(requests).then(() => {
        return this.userStore.apiUserData()
      }).then(() => {
        this.userStore.fetchMyClients(true)
        Helpers.emitter.emit('success', 'gespeichert')
      }).catch(() => {
        Helpers.emitter.emit('error', 'Fehler beim Speichern')
      })
    },
    async submitPassword(values, {resetForm}) {
      this.passwordSaving = true

      try {
        await this.userStore.apiChangeOwnPassword(
            values.current_password,
            values.password,
            values.password_confirmation,
        )
        resetForm()
        await this.userStore.clearSession({
          redirectToLogin: true,
          remoteLogout: false,
          reason: 'password_changed',
        })
      } catch (error) {
        const message = error.response?.data?.message
        Helpers.emitter.emit(
            'error',
            Array.isArray(message) ? message.join(' ') : message || 'Das Passwort konnte nicht geändert werden.'
        )
      } finally {
        this.passwordSaving = false
      }
    },
    async requestAccountDeletion() {
      if (!this.canRequestAccountDeletion || this.deletionRequestSending || this.userStore.user.deletion_request_pending) return
      this.deletionRequestSending = true
      try {
        const confirmation = await Swal.fire({
          title: 'Account-Löschung anfragen?',
          text: 'Die Anfrage umfasst Ihren Account und sämtliche zugehörigen Kundendaten einschließlich Veröffentlichungen. Unser Team prüft den Umfang und gemeinsame Kundenzuordnungen vor der Löschung.',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Löschung anfragen',
          cancelButtonText: 'Abbrechen',
          focusCancel: true,
        })
        if (!confirmation.isConfirmed) return
        const response = await this.userStore.apiRequestAccountDeletion()
        if (response.data.error) throw new Error(response.data.message)
        this.userStore.user.deletion_request_pending = true
        Helpers.emitter.emit('success', 'Ihre Löschanfrage wurde aufgenommen und wird geprüft.')
      } catch (error) {
        Helpers.emitter.emit('error', error.response?.data?.message || 'Die Löschanfrage konnte nicht gesendet werden. Bitte versuchen Sie es erneut.')
      } finally {
        this.deletionRequestSending = false
      }
    },
    async downloadPersonalData() {
      this.personalDataDownloading = true

      try {
        const response = await this.userStore.apiPersonalDataExport()
        const downloadUrl = URL.createObjectURL(response.data)
        const link = document.createElement('a')
        link.href = downloadUrl
        link.download = `manet-marketing-personal-data-${new Date().toISOString().slice(0, 10)}.json`
        document.body.appendChild(link)
        link.click()
        link.remove()
        URL.revokeObjectURL(downloadUrl)
      } catch (_) {
        Helpers.emitter.emit('error', 'Die persönlichen Daten konnten nicht heruntergeladen werden.')
      } finally {
        this.personalDataDownloading = false
      }
    },
  },
}
</script>

<style scoped>
.account-password,
.account-personal-data {
  border-top: 1px solid rgba(12, 58, 83, 0.12);
}
</style>
